import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'

import { Button, Loading } from '../root/components/common'
import * as actions from './actions'
import { UserDetails } from './components/user-details'
import { CustomerTabs } from './components/customer-tabs'
import { UserLocations } from './components/user-locations'
import { useState } from 'react'
import { useBooleanState } from 'web/src/utils/hooks/useBooleanState'
import { patchUser } from './api/users-api'
import { DEFAULT_ROLES } from '../root/constants'
import { getSagasMsgs } from '../root/selectors'
import { enqueueSnackbar } from '../root/actions'
import { UserPermissions } from 'web/src/services/permissions'
import Input from '../root/components/common/input'

function UserProfile(props) {
    const userId = props.match.params.id
    const { user: selectedUser, isLoadingUser } = useSelector(
        state => state.userManagement
    )
    const [user, setUser] = useState({})
    const [locationSearchStr, setLocationSearchStr] = useState('')
    const { locale, user: currentUser } = useSelector(state => state.main)
    const sagasMsgs = useSelector(getSagasMsgs)
    const saving = useBooleanState(false)
    const pristine = useBooleanState(true)
    const dispatch = useDispatch()
    const styles = useStyles()
    const isCustomerAdmin =
        currentUser?.original_role === DEFAULT_ROLES.customer_admin
    const isTraplinkedAdmin =
        currentUser?.original_role === DEFAULT_ROLES.traplinked_admin
    const isSelectedExternalUser = selectedUser?.is_external
    const isCurUserAdmin = isCustomerAdmin || isTraplinkedAdmin
    const canEdit =
        UserPermissions.isUserMgmtGranted() && !isSelectedExternalUser

    const fetchUser = () => dispatch(actions.userRequest(userId))

    const rolesMap = useSelector(state =>
        state.userManagement.roles
            ? state.userManagement.roles.reduce((acc, role) => {
                  acc[role.code] = role
                  return acc
              }, {})
            : null
    )

    const handleUserUpdate = v => {
        pristine.off()
        setUser(u => ({
            ...u,
            user: {
                ...u.user,
                ...v
            }
        }))
    }

    const handleRolesUpdate = v => {
        pristine.off()
        setUser(u => ({
            ...u,
            roles: [v]
        }))
    }

    const handleLocationsUpdate = locations => {
        pristine.off()
        setUser(u => ({
            ...u,
            locations
        }))
    }

    const handleLocationSearch = searchStr => {
        setLocationSearchStr(searchStr)
    }

    const handleSave = () => {
        saving.on()
        patchUser(selectedUser.id, user)
            .then(r => {
                dispatch(
                    enqueueSnackbar({
                        message: sagasMsgs.updateUserRes,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            })
            .catch(e => {
                dispatch(
                    enqueueSnackbar({
                        message: sagasMsgs.updateUserEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            })
            .finally(() => {
                fetchUser()
                saving.off()
            })
        return
    }

    useEffect(() => {
        fetchUser()
    }, [])

    useEffect(() => {
        if (!selectedUser) {
            return
        }
        // init user model + assigned locations (the single source of truth
        // sent to the backend). Seeded from ALL customers' assigned locations
        // so search/customer-tab switching never drops assignments.
        handleUserUpdate(getUserDetail(selectedUser))
        setUser(u => ({
            ...u,
            locations: getAllAssignedIds(selectedUser.locations)
        }))
        pristine.on()
    }, [selectedUser])

    if (isLoadingUser) {
        return <Loading />
    }

    if (!selectedUser || !user.user) {
        return null
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% - 65px)'
            }}
        >
            <div className={styles.header}>
                <div className={styles.title}>{selectedUser.name}</div>
                {UserPermissions.isUserMgmtGranted() && (
                    <Button disabled={pristine.is} onClick={handleSave}>
                        {locale.common.save}
                    </Button>
                )}
            </div>
            <div
                style={{
                    display: 'flex',
                    height: '100%'
                }}
            >
                <UserDetails
                    disabled={!canEdit}
                    email={selectedUser.email}
                    user={user}
                    updateUser={handleUserUpdate}
                    updateRole={handleRolesUpdate}
                    rolesMap={rolesMap}
                />
                {UserPermissions.isUserMgmtGranted() ? (
                    <div className={styles.locationsSharing}>
                        <div
                            style={{
                                marginBottom: 40,
                                fontSize: 20,
                                letterSpacing: '-1.6px',
                                fontWeight: 'bold'
                            }}
                        >
                            {locale.common.assignment}
                        </div>
                        <CustomerTabs locations={selectedUser.locations}>
                            {customerId => (
                                <>
                                    <Input
                                        label={locale.common.search}
                                        type="text"
                                        setValue={handleLocationSearch}
                                        debounce={600}
                                        value={locationSearchStr}
                                        size="small"
                                        className={styles.locatonsSearchInput}
                                    />
                                    <UserLocations
                                        canAssign={canUpdateAssignedLocations(
                                            currentUser,
                                            customerId
                                        )}
                                        onAssignedUpdate={handleLocationsUpdate}
                                        userId={selectedUser.id}
                                        assignedLocations={user.locations}
                                        customerId={customerId}
                                        locations={selectedUser.locations}
                                        locationSearchStr={locationSearchStr}
                                    />
                                </>
                            )}
                        </CustomerTabs>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

// Collect every assigned location id across all customers. This is the
// source of truth for the assignment payload; it must never be derived from
// the filtered/visible subset.
const getAllAssignedIds = locationsByCustomer =>
    Object.values(locationsByCustomer || {}).flatMap(customer =>
        (customer.locations || []).filter(l => l.is_assigned).map(l => l.id)
    )

export const getUserDetail = user => {
    if (user) {
        return {
            language: user.language,
            name: user.name,
            role_id: user.role_id
        }
    }
    return {}
}

const canUpdateAssignedLocations = (currentUser, locationCustomerId) => {
    return (
        Number(currentUser.original_customer_id) === Number(locationCustomerId)
    )
}

const useStyles = makeStyles({
    locationsSharing: {
        flex: 1,
        // marginTop: '-25px',
        marginLeft: '-34px',
        // marginRight: '-50px',
        marginBottom: '60px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 40
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        letterSpacing: '-1.6px'
    },
    locatonsSearchInput: {
        paddingLeft: 6,
        paddingRight: 28
    }
})

export default withRouter(UserProfile)
